import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, memo } from 'react';
import { injectIntl } from 'react-intl';
import { DrawerActionHandler } from 'components/util/drawer-action-handler-component';

import FormItem from 'components/common/form-item-component';
import { TooltipOptions } from 'models/utils/common/tooltip-options-model';

import { ReactComponent as ClearButton } from 'icons/close-button.svg';
import { ReactComponent as SearchButton } from 'icons/search.svg';
import { ReactComponent as ArrowDown } from 'icons/arrow-simple-thick-down.svg';

import {
  ENTER_KEY_CODE,
  SPACEBAR_KEY_CODE,
} from 'constants/util/key-code-constants';
import { isNullOrUndefined } from 'utils/object-utils';

const SEARCH_BLOCK = 'search';
const SEARCH_CLOSE_ICON_SIZE = '28px';
const SEARCH_ICON_SIZE = '20px';
const ARROW_DOWN_SIZE = '18px';

const DefaultSearch = ({
  className,
  disabled,
  errorText,
  id,
  intl,
  isOpen,
  isValid,
  clearIconSize = SEARCH_CLOSE_ICON_SIZE,
  isSearchableDropdown = false,
  label,
  maxLength,
  onBlur,
  onKeyDown,
  onChange,
  onSearch,
  onClear,
  onFocus,
  placeholder,
  setWrapperRef,
  showSearchButton,
  toggleDrawer,
  tooltip,
  value,
  width,
  children, // NOTE: do not use externally, only used internally by SearchDropdown and SearchDropdownApi components
  disableAutoComplete,
}) => {
  const searchInputRef = useRef();
  const _onSearch = () => onSearch(value);

  return (
    <FormItem
      className={className}
      disabled={disabled}
      errorText={errorText}
      id={id}
      isValid={isValid}
      label={label}
      tooltip={tooltip}
      width={width}
    >
      <div
        ref={setWrapperRef}
        className={`${SEARCH_BLOCK}__container`}
        disabled={disabled}
      >
        <div
          onKeyDown={(e) => {
            if (!isNullOrUndefined(onSearch) && e.key === 'Enter') {
              _onSearch();
            }
            onKeyDown(e);
          }}
          className={`${SEARCH_BLOCK}__bar`}
        >
          {showSearchButton ? (
            <div
              className={`${SEARCH_BLOCK}__search-button`}
              disabled={disabled}
            >
              <SearchButton
                name={'search-button'}
                role={'button'}
                className={`${SEARCH_BLOCK}__search-icon`}
                width={SEARCH_ICON_SIZE}
                height={SEARCH_ICON_SIZE}
                onClick={() => {
                  if (!isNullOrUndefined(onSearch)) {
                    _onSearch();
                  }
                  searchInputRef.current.focus();
                }}
              />
            </div>
          ) : null}
          <div
            className={classNames(
              SEARCH_BLOCK,
              !isValid ? `${SEARCH_BLOCK}--invalid` : null,
            )}
            disabled={disabled}
          >
            <input
              className={`${SEARCH_BLOCK}__input`}
              disabled={disabled}
              id={id}
              label={label}
              maxLength={maxLength}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onKeyDown={(e) =>
                e.keyCode === ENTER_KEY_CODE ? onChange(value) : null
              }
              onBlur={onBlur}
              placeholder={intl.formatMessage({ id: placeholder })}
              type={'search'}
              value={value}
              width={width}
              onFocus={onFocus}
              tabIndex={0}
              ref={searchInputRef}
              autoComplete={disableAutoComplete && 'off'}
            />
            {value ? (
              <ClearButton
                name={'close-button'}
                role={'button'}
                className={`${SEARCH_BLOCK}__close`}
                width={clearIconSize}
                height={clearIconSize}
                onClick={() => {
                  if (!disabled) {
                    onClear();
                  }
                }}
                tabIndex={!disabled ? 0 : null}
                onKeyDown={(e) =>
                  e.keyCode === SPACEBAR_KEY_CODE && !disabled
                    ? onClear()
                    : null
                }
                disabled={disabled}
              />
            ) : null}
          </div>
          {isSearchableDropdown && !value && (
            <div
              className={`${SEARCH_BLOCK}__arrow-icon`}
              onClick={toggleDrawer}
            >
              <ArrowDown
                name={'arrow-simple-thick-down'}
                width={ARROW_DOWN_SIZE}
                height={ARROW_DOWN_SIZE}
                style={isOpen ? { transform: 'rotate(180deg)' } : null}
                disabled={disabled}
              />
            </div>
          )}
        </div>
        {children}
      </div>
    </FormItem>
  );
};

const sharedPropTypes = {
  /** String custom class */
  className: PropTypes.string,
  /** Boolean representing if component should be disabled state */
  disabled: PropTypes.bool,
  /** Unique string id for search */
  id: PropTypes.string.isRequired,
  /** HOC Boolean representing whether search menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Internationalized Sting id passed to form item */
  label: PropTypes.string,
  /** Maximum search query length */
  maxLength: PropTypes.number,
  /** Function fired on blur*/
  onBlur: PropTypes.func,
  /** Function fired on user input into search, used to track search state variable
     * onChange should be doing some kind of this.setState() or similar redux actions to control
     * the value prop. However, if an API request is necessary as well it should be debounced and passed as
     * the 2nd argument to that set state. Which means it would look something like below:
     * onChange={val => {
        this.setState({ search: val }, () =>
          this.debouncedSearch({ search: this.state.search }),
        );
      }}
    */
  onChange: PropTypes.func.isRequired,
  /** Optional function fired when either the search button is clicked or enter key is pressed while search has focus */
  onSearch: PropTypes.func,
  /** HOC function fired for key presses, needed to make dropdown accessible */
  onKeyDown: PropTypes.func.isRequired,
  /** HOC Function fired on focus */
  onFocus: PropTypes.func.isRequired,
  /** Internationalized Sting id passed to form item */
  placeholder: PropTypes.string,
  /** Boolean representing whether search button is shown */
  showSearchButton: PropTypes.bool,
  /** Function fired to toggle search menu */
  toggleDrawer: PropTypes.func.isRequired,
  /** Object with tool tip options passed to form item */
  tooltip: PropTypes.instanceOf(TooltipOptions),
  /** String value of the search bar */
  value: PropTypes.string,
  /** String percentage representing width of component passed to form item*/
  width: PropTypes.string,
  /** indicates if menu should use a react portal or normal child element implementation */
  usePortalMenu: PropTypes.bool,
};

DefaultSearch.propTypes = {
  ...sharedPropTypes,
  /** (Should not be used externally! Use SearchApi component instead) Boolean flag passed by SearchApi component below to use the ApiMenu */
  useApiMenu: PropTypes.bool,
};

export default memo(
  DrawerActionHandler(injectIntl(DefaultSearch), {
    textInputDrawer: true,
  }),
);
